import * as React from "react";
import { styled } from "styled-components";

const size = "1.5rem";

const RadioContainer = styled.div`
  position: relative;
  padding-left: ${size};
`;

const RadioLabel = styled.div`
  font-weight: bold;
`;

const RadioDescription = styled.div`
  margin-top: 0.5rem;
`;

const RadioText = styled.label<{ $error?: boolean }>`
  margin: 0 0 0 0.5rem;
  display: inline-block;
  cursor: pointer;
  touch-action: manipulation;
  transition: all 200ms ease-in-out;

  & * {
    cursor: pointer;
  }

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: ${size};
    height: ${size};
    border: 1px solid ${(props) => (!props.$error ? props.theme.colours.grey[400] : props.theme.colours.error)};
    background: ${(props) => (!props.$error ? "transparent" : props.theme.colours.red[50])};
    border-radius: 50%;
    transition: all 200ms ease-in-out;
    box-shadow: 0 0 0 0 ${(props) => props.theme.colours.focus};
  }

  &:after {
    content: "";
    position: absolute;
    top: 6px;
    left: 6px;
    width: 0;
    height: 0;
    border: 6px solid ${(props) => props.theme.colours.primary};
    border-radius: 50%;
    transition: all 200ms ease-in-out;
    opacity: 0;
  }
`;

export const RadioInput = styled.input.attrs({ type: "radio" })`
  cursor: pointer;
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  width: ${size};
  height: ${size};
  margin: 0;
  opacity: 0;
  transition: all 200ms ease-in-out;

  &:checked + ${RadioText} {
    &:before {
      border-color: ${(props) => props.theme.colours.grey[400]};
      background: transparent;
    }
    &:after {
      opacity: 1;
    }
  }

  &:focus + ${RadioText} {
    &:before {
      box-shadow: 0 0 0 3px ${(props) => props.theme.colours.focus};
    }
  }

  &:hover + ${RadioText} {
    &:before {
      border-color: ${(props) => props.theme.colours.grey[500]};
    }
  }
`;

export const RadioButton: React.FC<React.ComponentProps<typeof RadioInput> & { label?: string; error?: unknown }> = ({
  children,
  label,
  error,
  ...otherProps
}) => {
  const { id } = otherProps;

  return (
    <RadioContainer>
      <RadioInput id={id} {...otherProps} />
      <RadioText htmlFor={id} $error={Boolean(error)}>
        <RadioLabel>{label}</RadioLabel>
        {children && <RadioDescription>{children}</RadioDescription>}
      </RadioText>
    </RadioContainer>
  );
};
